// src/pages/Admin/AppSettings.js
import React, { useEffect, useState } from "react";
import { API_KEY, CRUDadmin as CRUDadminURL } from "../../hooks/APIHooks";
import LogoLoader from "../../components/LogoLoader/LogoLoader";

const PASSWORD = "123456789";

const fetchCRUDadmin = async (method, table, id = null, data = null) => {
  const url = new URL(CRUDadminURL);
  url.searchParams.append("table", table);
  url.searchParams.append("api_key", API_KEY);
  if (id) url.searchParams.append("id", id);

  const options = { method, headers: {} };

  if (data && (method === "POST" || method === "PUT")) {
    options.headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(data);
  }

  const response = await fetch(url, options);
  return await response.json();
};

const AppSettings = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");

  const [companySettings, setCompanySettings] = useState({
    id: 1,
    companylogo: "",
    companyName: "",
    companyTagLine: "",
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    companyWhatsapp: "",
    companyAddress: "",
    upi_id: "",
    all_day_payments: "false",
  });

  const [upiImageFile, setUpiImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    if (!authenticated) return;

    const fetchCompanySettings = async () => {
      setLoading(true);
      try {
        const response = await fetchCRUDadmin("GET", "companySettings", 1);
        if (response.success && response.data.length > 0) {
          setCompanySettings(response.data[0]);
        } else {
          setResponseMessage(response.message || "No data found.");
        }
      } catch (error) {
        console.error("Error fetching company settings", error);
        setResponseMessage("Error fetching company settings");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanySettings();
  }, [authenticated]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompanySettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setUpiImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let upiImageFilename = companySettings.upi_id;

    if (upiImageFile) {
      const formData = new FormData();
      formData.append("file", upiImageFile);

      const uploadRes = await fetch(
        "https://marketing.thewinnerindia.com/apis/admin_apis/upload.php",
        {
          method: "POST",
          body: formData,
        }
      );

      const uploadData = await uploadRes.json();
      if (uploadData.success) {
        upiImageFilename = `https://marketing.thewinnerindia.com/apis/admin_apis/${uploadData.filename}`;
      } else {
        setResponseMessage("Image upload failed");
        return;
      }
    }

    try {
      const updatedSettings = {
        ...companySettings,
        upi_id: upiImageFilename,
      };

      const response = await fetchCRUDadmin(
        "PUT",
        "companySettings",
        companySettings.id,
        updatedSettings
      );
      setResponseMessage(response.message);
    } catch (error) {
      console.error("Error updating settings", error);
      setResponseMessage("Failed to update settings");
    }
  };

  if (!authenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (password === PASSWORD) {
              setAuthenticated(true);
            } else {
              alert("Incorrect password");
            }
          }}
          className="bg-white p-6 rounded shadow-md w-full max-w-xs"
        >
          <h2 className="text-xl font-bold mb-4 text-center text-secondary">
            Enter Admin Password
          </h2>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded mb-4"
          />
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  if (loading) return <LogoLoader isLoading={true} />;

  return (
    <div className="container mx-auto p-4 max-w-2xl my-10">
      <h1 className="text-3xl font-bold text-secondary text-center">
        App Settings
      </h1>

      {responseMessage && (
        <div
          className={`mt-4 p-3 rounded text-center ${
            responseMessage.toLowerCase().includes("success") ||
            responseMessage.toLowerCase().includes("updated")
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {responseMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        {/* Company Info Fields */}
        {[
          {
            label: "Company Name",
            name: "companyName",
            type: "text",
            required: true,
          },
          { label: "Company Tagline", name: "companyTagLine", type: "text" },
          {
            label: "Company Phone",
            name: "companyPhone",
            type: "text",
            required: true,
          },
          {
            label: "Company Email",
            name: "companyEmail",
            type: "email",
            required: true,
          },
          { label: "Company Website", name: "companyWebsite", type: "url" },
          { label: "Company Whatsapp", name: "companyWhatsapp", type: "text" },
        ].map(({ label, name, type, required }) => (
          <label key={name} className="block">
            <span className="text-white">{label}:</span>
            <input
              type={type}
              name={name}
              value={companySettings[name]}
              onChange={handleChange}
              required={required}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </label>
        ))}

        {/* UPI Image Upload */}
        <label className="block">
          <span className="text-white">UPI QR Image:</span>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          />
          {companySettings.upi_id && (
            <img
              src={companySettings.upi_id}
              alt="UPI"
              className="mt-2 w-32 h-32 object-contain border"
            />
          )}
        </label>

        {/* Address */}
        <label className="block">
          <span className="text-white">Company Address:</span>
          <textarea
            name="companyAddress"
            value={companySettings.companyAddress}
            onChange={handleChange}
            required
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          />
        </label>

        {/* Withdraw Option */}
        <label className="block">
          <span className="text-white">Allow All-Day Withdrawals:</span>
          <input
            type="checkbox"
            name="all_day_payments"
            checked={companySettings.all_day_payments === "true"}
            onChange={(e) =>
              setCompanySettings((prev) => ({
                ...prev,
                all_day_payments: e.target.checked ? "true" : "false",
              }))
            }
            className="mt-1"
          />
        </label>

        <button
          type="submit"
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Update Settings
        </button>
      </form>
    </div>
  );
};

export default AppSettings;
